import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleXmark,
  faLightbulbOn,
  faPaperPlane,
} from "@fortawesome/pro-regular-svg-icons";
import cx from "classnames";
import Badge from "../../common/Badge";
import { CardFieldType, CoachCardStatus, PriorityCard } from "./types";
import { WEIGHT_CONFIG, SIGNAL_TYPE_LABELS } from "./config";
import { resolveTokens } from "./tokenUtils";

interface CoachPriorityCardProps {
  card: PriorityCard;
  isReadOnly: boolean;
  tokens: Record<string, string>;
  onStatusChange(id: number, status: CoachCardStatus): void;
  onCreateFeedback(
    cardId: number,
    value: "UP" | "DOWN",
  ): Promise<number | null>;
  onUpdateFeedbackText(feedbackId: number, text: string): Promise<void>;
}

export default function CoachPriorityCard({
  card,
  isReadOnly,
  tokens,
  onStatusChange,
  onCreateFeedback,
  onUpdateFeedbackText,
}: CoachPriorityCardProps) {
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSent, setNoteSent] = useState(false);
  const [feedbackId, setFeedbackId] = useState<number | null>(null);
  // True while the "Not useful" feedback form is open, before the card is actually dismissed
  const [pendingDismiss, setPendingDismiss] = useState(false);

  const isDiscussed = card.status === "DISCUSSED";
  const isDismissed = card.status === "DISMISSED";

  const statusIcon = isDiscussed
    ? faCircleCheck
    : isDismissed
      ? faCircleXmark
      : faLightbulbOn;
  const statusIconClass = isDiscussed
    ? "text-green-500"
    : isDismissed
      ? "text-slate-300"
      : "text-amber-400";

  const weightCfg = WEIGHT_CONFIG[card.weight];

  const resolve = (text: string) => resolveTokens(text, tokens);

  // Pull fields by type — UI controls display order, not sortOrder
  const whyItMatters = card.fields.find(
    (f) => f.fieldType === CardFieldType.WhyItMatters,
  );
  const watchOut = card.fields.find(
    (f) => f.fieldType === CardFieldType.WatchOut,
  );
  const desiredOutcome = card.fields.find(
    (f) => f.fieldType === CardFieldType.DesiredOutcome,
  );
  const prompts = card.fields
    .filter((f) => f.fieldType === CardFieldType.Prompt)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const handleDiscussed = async () => {
    const id = await onCreateFeedback(card.id, "UP");
    setFeedbackId(id);
    onStatusChange(card.id, "DISCUSSED");
  };

  // Step 1: open the feedback form — do NOT dismiss yet
  const handleNotUseful = () => {
    setPendingDismiss(true);
  };

  // Step 2: create feedback (+ optional text) then dismiss
  const handleConfirmDismiss = async () => {
    const id = await onCreateFeedback(card.id, "DOWN");
    if (id !== null && noteText.trim()) {
      await onUpdateFeedbackText(id, noteText);
    }
    onStatusChange(card.id, "DISMISSED");
    setPendingDismiss(false);
    setNoteText("");
  };

  // "Discussed" path: feedback record already created, just attach the note
  const handleSendNote = async () => {
    if (!noteText.trim()) return;
    if (feedbackId !== null) {
      await onUpdateFeedbackText(feedbackId, noteText);
    }
    setNoteSent(true);
    setNoteOpen(false);
    setNoteText("");
  };

  return (
    <div
      className={cx(
        "rounded-md border",
        isDiscussed
          ? "border-green-200 bg-green-50"
          : isDismissed
            ? "border-slate-100 bg-slate-50 opacity-60"
            : "border-slate-200 bg-white",
      )}
    >
      {/* Title section */}
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start gap-2.5">
          <FontAwesomeIcon
            icon={statusIcon}
            className={cx("mt-1 shrink-0", statusIconClass)}
          />
          <div className="flex-1 min-w-0">
            <h4 className="text-base font-semibold text-slate-700 leading-snug">
              {resolve(card.title)}
            </h4>

            <div className="flex items-center gap-1 mt-2">
              <Badge
                text={weightCfg.label}
                backgroundColourClassName={weightCfg.bg}
                textColourClassName={weightCfg.fg}
                marginClassName=""
                borderRadiusClassName="rounded"
                classNames="font-semibold"
              />
              <Badge
                text={SIGNAL_TYPE_LABELS[card.signalType]}
                backgroundColourClassName="bg-slate-100"
                textColourClassName="text-slate-500"
                marginClassName=""
                borderRadiusClassName="rounded"
              />
            </div>
          </div>
        </div>
      </div>

      <hr className="border-slate-100" />

      {/* Body — fields rendered in fixed display order */}
      <div className="px-4 py-3 space-y-3.5">
        {whyItMatters && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Why it matters
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              {resolve(whyItMatters.content)}
            </p>
          </div>
        )}

        {prompts.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
              Consider asking
            </p>
            <ul className="space-y-2">
              {prompts.map((p, i) => (
                <li
                  key={i}
                  className="text-sm text-slate-700 pl-3 border-l-2 border-blue-300 leading-relaxed"
                >
                  {resolve(p.content)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {watchOut && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Watch out for
            </p>
            <p className="text-sm text-slate-500 leading-relaxed">
              {resolve(watchOut.content)}
            </p>
          </div>
        )}

        {desiredOutcome && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">
              Useful outcome
            </p>
            <p className="text-sm text-slate-600 leading-relaxed">
              {resolve(desiredOutcome.content)}
            </p>
          </div>
        )}

        {/* Actions / feedback footer */}
        <div className="py-3 border-t border-slate-100 space-y-2">
          {/* Action buttons — hidden while pending dismiss, or once actioned */}
          {!isReadOnly && !isDiscussed && !isDismissed && !pendingDismiss && (
            <div className="flex gap-2">
              <button
                onClick={handleDiscussed}
                className="flex-1 flex items-center justify-center gap-1.5 text-sm text-green-700 hover:text-green-900 font-medium border border-green-200 hover:border-green-300 rounded-md py-1.5 transition-colors"
              >
                <FontAwesomeIcon icon={faCircleCheck} />
                Discussed
              </button>
              <button
                onClick={handleNotUseful}
                className="flex-1 flex items-center justify-center gap-1.5 text-sm text-red-500 hover:text-red-700 font-medium border border-red-200 hover:border-red-300 rounded-md py-1.5 transition-colors"
              >
                <FontAwesomeIcon icon={faCircleXmark} />
                Not useful
              </button>
            </div>
          )}

          {/* "Not useful" path: feedback form shown before the card is dismissed */}
          {pendingDismiss && (
            <div className="space-y-2">
              <textarea
                rows={2}
                autoFocus
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Optional — what was off?"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 text-slate-600 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-1 focus:ring-blue-300"
              />
              <div className="flex justify-end">
                <button
                  onClick={handleConfirmDismiss}
                  className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded transition-colors text-red-500 hover:text-red-700 hover:bg-red-50"
                >
                  <FontAwesomeIcon icon={faCircleXmark} />
                  {noteText.trim() ? "Send & dismiss" : "Dismiss"}
                </button>
              </div>
            </div>
          )}

          {/* "Discussed" path: subtle "Give feedback" link */}
          {isDiscussed && !noteSent && !noteOpen && (
            <button
              onClick={() => setNoteOpen(true)}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Give feedback
            </button>
          )}

          {/* "Discussed" path: note textarea + send */}
          {noteOpen && (
            <div className="space-y-2">
              <textarea
                rows={2}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Optional — anything worth noting?"
                className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 text-slate-600 placeholder:text-slate-300 resize-none focus:outline-none focus:ring-1 focus:ring-blue-300"
              />
              <div className="flex justify-end">
                <button
                  onClick={handleSendNote}
                  disabled={!noteText.trim()}
                  className={cx(
                    "flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded transition-colors",
                    noteText.trim()
                      ? "text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                      : "text-slate-300 cursor-not-allowed",
                  )}
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                  Send feedback
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
